/**
 * The ONE home of the layer predicates every analysis entry point shares
 * (analysis-launcher spec, Interface contract). The registry's
 * `acceptsContext` / `prerequisite`, the launch hook's candidate lists and
 * the dialogs all read from here, so "which layers can feed X" cannot drift
 * between the context menu, the launcher and the dialog's own pickers.
 *
 * `isSurfaceCandidate`, `isDeviationReferenceCandidate` and
 * `isClearanceCandidate` were moved here verbatim from their dialogs, which
 * re-export them so existing importers (and test mocks of those modules) keep
 * working.
 */
import type { LayerData } from '@/atoms/layers.atom';

/** The structural shape the predicates accept — the dialogs pass slimmer
 *  layer objects than `LayerData` (e.g. `RunClearanceLayer`). */
export type LayerLike = { type?: string | null; metadata?: unknown };

const metadataType = (layer: LayerLike | undefined): string =>
  (layer?.metadata as { type?: string } | null | undefined)?.type ?? '';

export const isPointcloudLayer = (layer: { type?: string | null }): boolean => layer.type === 'POINTCLOUD';

export const isVectorLayer = (layer: { type?: string | null }): boolean => layer.type === 'VECTOR';

/** Panorama / image layers — the IPRJ export packs their TopoDOT project and images. */
export const isImageLayer = (layer: { type?: string | null }): boolean => layer.type === 'IMAGE';

/** IFC models — either the raw `.ifc` (legacy `IFC`) or the fragment-converted
 *  tileset (`FRAGMENT`, the current upload path). */
export const isIfcModelLayer = (layer: LayerLike | undefined): boolean =>
  layer?.type === 'MODEL' && ['IFC', 'FRAGMENT'].includes(metadataType(layer));

export const isLandxmlLayer = (layer: LayerLike | undefined): boolean =>
  layer?.type === 'MODEL' && metadataType(layer) === 'LANDXML';

/** An existing, completed LandXML model that the merge API will accept. */
export const isReadyLandxmlMergeSource = (layer: LayerLike & {
  root?: string | null; isHidden?: boolean | null; isEphemeral?: boolean | null;
} | undefined): boolean =>
  !!layer && isLandxmlLayer(layer) && !!layer.root && !layer.isHidden && !layer.isEphemeral;

/** Layers a surface difference / layer stack can read as a surface. */
export const isSurfaceCandidate = (l: LayerData) =>
  l.type === 'POINTCLOUD' ||
  l.type === 'VECTOR' ||
  (l.type === 'MODEL' && ['LANDXML', 'DXF', 'IFC', 'FRAGMENT'].includes((l.metadata as any)?.type ?? ''));

/** MODEL sub-types the deviation worker can read as a reference mesh. XGRIDS /
 *  SPLAT layers are gaussian splats with no surface to measure against. */
const DEVIATION_MODEL_REFERENCE_TYPES = ['IFC', 'FRAGMENT', 'LANDXML'];

/** Layers that can be offered as a deviation reference. Shared by every
 *  entry point that opens the deviation dialog so the list stays consistent. */
export const isDeviationReferenceCandidate = (layer: LayerLike): boolean => {
  if (layer.type === 'VECTOR' || layer.type === 'POINTCLOUD') return true;
  if (layer.type === 'MODEL') return DEVIATION_MODEL_REFERENCE_TYPES.includes(metadataType(layer));
  return false;
};

/** The Compare surfaces A/B pool: every layer either engine can read. One
 *  predicate for the launcher's opener and the re-run opener, so the two
 *  dialogs' pools cannot drift. */
export const isCompareCandidate = (l: LayerData): boolean => isSurfaceCandidate(l) || isDeviationReferenceCandidate(l);

/** The clearance check classifies ONE point cloud per run (spec §Inputs). */
export const isClearanceCandidate = (layer: { type?: string | null }): boolean => layer.type === 'POINTCLOUD';
